import isNil from 'lodash/isNil';
import {
	type CacheConfig,
	type GraphQLResponse,
	type GraphQLSingularResponse,
	Observable,
	type PayloadError,
	type RequestParameters,
	type Variables,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { putExtensions } from '@atlassian/relay-capture-extensions';
import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { CompletedQueryPromisesMap, QueryPromisesMap } from '@atlassian/relay-query-promises';
import { type SSRQueryResponseCache } from '@atlassian/relay-query-responses';
import { addTraceId } from '@atlassian/relay-traceid';
import { getClientDispatcher } from '@atlassian/server-to-client-comms/client-dispatcher';
import { registerEvent } from '@atlassian/server-to-client-comms/server-receiver';

import { buildGraphqlSmartFetch, processMultipartPromise } from './multipart-fetch';
import { type GraphQLMultipartResponsePayload } from './multipart-fetch/types';
import { getRetryAttemptsForQuery, retryOnError, shouldRetry } from './utils/retries';
import { getTraceIdFromExtensions } from './utils/trace';

type GraphqlQueryDetails = {
	queryName: string;
	operationKind: string;
};

type LoggingAndMetricsHelper = {
	logNonBreakingErrors: (errors?: PayloadError[] | null, requestId?: string) => void;
	logError: (error?: Error, retryAttempt?: number) => void;
	logErrorRetry: (error?: Error, retryAttempt?: number) => void;
};

const isSsrHandoverIncrementsEnabled = () => Boolean((globalThis as any).__SSR_HANDOVER_INCREMENTS);

const moduleID = 'relay-fetch';

export type ExternalDependencies = {
	getLoggingAndMetricsHelper?: (graphqlDetails: GraphqlQueryDetails) => LoggingAndMetricsHelper;
	withErrorHandling?: (source: Promise<Response>) => Promise<Response>;
	setEndpoint?: (endpoint: string) => string;
	/**
	 * This is a function that products can provide to perform a preflight authentication check before the fetch is initiated.
	 * This is intended to be used in cases where the product knows that the user is unauthenticated (e.g. from a previous fetch attempt, or from a cookie check), and wants to trigger the authentication flow (e.g. redirect to login page) before starting the fetch with an invalid auth state.
	 * The function should return `true` if it triggered a redirect for authentication, and `false` otherwise.
	 * LDR: https://hello.atlassian.net/wiki/spaces/JFP/pages/7144999442/LDR+Introduce+Authentication+Check+For+Platform+Relay+Fetch
	 */
	didPreflightAuthenticationCheckTriggerRedirect?: (
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	) => boolean;
	validateResponse?: (request: RequestParameters, data: GraphQLSingularResponse) => boolean;
	onInvalidResponse?: (request: RequestParameters, response: GraphQLSingularResponse) => void;
	getHeaders?: (params: {
		endpoint: string;
		options: RequestInit;
		request: RequestParameters;
		variables: Variables;
	}) => HeadersInit;
	earlyScriptPromise?: (
		request: RequestParameters,
		variables: Variables,
	) => Promise<Response> | undefined;
	handleAbort?: (request: RequestParameters, reason: string) => void;
	/**
	 * If provided, this is called to get the AbortSignal before each fetch, to be able to terminate fetches early.
	 */
	getAbortSignal?: () => AbortSignal | null;
	queryResponseCache?: SSRQueryResponseCache | Omit<SSRQueryResponseCache, 'appendWithMetadata'>;
	/**
	 * Called whenever the fetch pipeline is about to surface an error to relay.
	 * Lets products transform / decorate / replace the error before it is
	 * forwarded to the relay `Sink<>` (e.g. attach `error.status` from
	 * `response.status`, wrap into a typed `FetchError`, add logging context).
	 * The returned value is what is forwarded to relay.
	 *
	 * `response` is `undefined` for failures that occur *before* an HTTP
	 * `Response` was received (e.g. network failure inside `fetchFn()` or an
	 * `AbortController` signal).
	 *
	 * If omitted, the original error is forwarded unchanged.
	 */
	onFetchError?: (error: unknown, response?: Response) => unknown;
	isServer: boolean;
};

export const fetch = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
	dependencies?: ExternalDependencies,
): Observable<GraphQLResponse> => {
	if (fg('nike_redirect-logic-jira')) {
		if (
			dependencies?.didPreflightAuthenticationCheckTriggerRedirect?.(
				request,
				variables,
				cacheConfig,
			) === true
		) {
			// Jira's preflight check starts login navigation as a side effect. Keep Relay pending
			// while the redirect takes over instead of starting a fetch with a stale auth state.
			return Observable.from(new Promise<GraphQLResponse>(() => {}));
		}
	}

	const { name: queryName, operationKind } = request;
	const loggingMetadata = {
		queryName,
		operationKind,
	};
	const loggingAndMetrics = dependencies?.getLoggingAndMetricsHelper?.(loggingMetadata);

	function augmentResponse(response: GraphQLSingularResponse) {
		addTraceId(queryName, getTraceIdFromExtensions(response));
		putExtensions(queryName, response.extensions);
	}

	// TODO: This needs to be changed by removing each Product, and only if all keys are empty final output is null
	function enforceNonNullability(response: GraphQLSingularResponse) {
		if (
			response.data &&
			(response.data.jira === null ||
				response.data.customerService === null ||
				response.data.graphStore === null)
		) {
			// Enforce non-nullability of the jira and customerService namespaces. This is an intermediary solution to
			// prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
			// to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
			// @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Data
			response.data = null;
		}
	}

	return Observable.create<GraphQLResponse>((sink) => {
		try {
			const requestID =
				'cacheID' in request && request.cacheID != null ? request.cacheID : request.id;
			// Unique identifier for the server-to-client channel.
			const sinkID = `${moduleID}-${requestID}`;

			let resolveCompletedResponse: () => void;
			const completedResponse: Promise<void> = new Promise((resolve) => {
				resolveCompletedResponse = resolve;
			});

			const finalise = () => {
				resolveCompletedResponse();
				sink.complete();
			};

			const handleResponse = (response: GraphQLMultipartResponsePayload) => {
				let payload: GraphQLSingularResponse | null = null;
				if ('incremental' in response && response.incremental) {
					if (response.incremental.length) {
						// Relay is expecting the contents of this array rather than the entire array itself. Also, Relay doesn't really care about tracking
						// the pending versus completed values, so we can just return the array without the extra account keeping.
						// @ts-expect-error It's hard to type this correctly because the payload sometimes has errors, sometimes not. Relay accounts for it,
						// TypeScript can't handle the polymorphism.
						payload = response.incremental;
					} else if (response.hasNext === false) {
						// It's possible for the server to send back an empty final increment.
						// Attempt to make Relay happy.
						payload = {
							data: null,
							extensions: { is_final: true },
						};
					}
				} else {
					// @ts-expect-error Typescript isn't happy that sometimes errors is undefined, but relay will manage just fine.
					payload = response;
				}

				// Notify observers that we have more data!
				if (payload) {
					sink.next(payload);
				}

				// Close the stream if there are no more responses.
				if ('hasNext' in response && response.hasNext === false) {
					finalise();
				}
			};

			// handles getting multipart response
			const getData = () => {
				const endpoint = getAggEndpoint(request, cacheConfig);

				const body = getRequestBody(request, variables);
				let options: RequestInit = { body };

				if (dependencies?.getHeaders) {
					const headers = dependencies.getHeaders({ endpoint, options, request, variables });
					options = { ...options, headers: { ...options.headers, ...headers } };
				}
				if (dependencies?.getAbortSignal) {
					options.signal = dependencies?.getAbortSignal();
				}

				const fetchOverNetwork: () => Promise<Response> = buildGraphqlSmartFetch({
					url: dependencies?.setEndpoint ? dependencies?.setEndpoint(endpoint) : endpoint,
					options,
					variables,
					request,
					isServer: dependencies?.isServer || false,
					cacheConfig,
				});

				const earlyScriptPromise = dependencies?.earlyScriptPromise?.(request, variables);

				let fetchFn = fetchOverNetwork;
				let initialSource = fetchFn;
				if (dependencies?.withErrorHandling) {
					const withErrorHandling = dependencies.withErrorHandling;
					initialSource = () =>
						!dependencies?.isServer && earlyScriptPromise
							? withErrorHandling(earlyScriptPromise)
							: withErrorHandling(fetchFn());
				}

				const responses = processMultipartPromise(
					() =>
						retryOnError(initialSource, {
							retryFunc: fetchFn,
							retryPredicate: shouldRetry,
							retryAttempts: getRetryAttemptsForQuery(request),
							onRetry: loggingAndMetrics?.logErrorRetry,
						}),
					dependencies?.onFetchError,
				);

				// Recreate a bizarre behaviour that will cause the observable to hang indefinitely when the user is not authenticated, presumably because we redirect the user to log in...
				// This should be revised; see https://jplat.atlassian.net/browse/MAGMA-3087
				let responseShouldHang = false;

				let firstResponseUsed = false;
				let resolveFirstResponse: () => void;
				const firstResponse: Promise<void> = new Promise((resolve) => {
					resolveFirstResponse = resolve;
				});

				responses.subscribe({
					start: () => {
						if (requestID != null) {
							if (dependencies?.isServer) {
								// so that SSR SPA knows it is safe to render
								const renderAfterThisResponse = isSsrHandoverIncrementsEnabled()
									? firstResponse
									: completedResponse;
								QueryPromisesMap.set(requestID, renderAfterThisResponse);
							} else {
								QueryPromisesMap.set(requestID, firstResponse);
							}
							CompletedQueryPromisesMap.set(requestID, completedResponse);
						}
					},
					next: (r) => {
						let payload = r;
						let response = (payload || {}) as GraphQLSingularResponse;

						if (dependencies?.isServer && isSsrHandoverIncrementsEnabled() && firstResponseUsed) {
							// Send the response payload to a side-channel.
							// TODO MAGMA-3204: improve robustness. Should be sending ID of module to communicate with, sinkID should be an argument.
							registerEvent({
								id: sinkID,
								name: 'default',
								args: [response],
							});

							if ('hasNext' in response && response.hasNext === false) {
								finalise();
							}

							// Don't send any further responses to the fetch subscriber, since we'll be sending them through a side-channel.
							return;
						}

						if (!firstResponseUsed) {
							firstResponseUsed = true;
							// according to spec, we can assume the first response is a GraphQLSingularResponse.
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const singularResponse = response as GraphQLSingularResponse;

							augmentResponse(singularResponse);
							if (
								dependencies?.validateResponse &&
								!dependencies.validateResponse(request, singularResponse)
							) {
								dependencies?.onInvalidResponse?.(request, singularResponse);
								responseShouldHang = true;
								return;
							}

							if (fg('platform_relay_use_new_non_nullability')) {
								response = enforceProductNonNullability(singularResponse);
							} else {
								enforceNonNullability(singularResponse);
							}

							// write cache query response on SSR
							// only write cache query response once or else this will break the assumption of graphql response being singular response
							if (
								dependencies?.isServer &&
								requestID != null &&
								typeof dependencies?.queryResponseCache !== 'undefined'
							) {
								if ('appendWithMetadata' in dependencies.queryResponseCache) {
									dependencies.queryResponseCache.appendWithMetadata(
										requestID,
										variables,
										singularResponse,
										loggingMetadata,
									);
								} else {
									dependencies.queryResponseCache.setWithMetadata(
										requestID,
										variables,
										singularResponse,
										loggingMetadata,
									);
								}
							}
						}

						handleResponse(response);

						// signal that the initial response has been received
						resolveFirstResponse();

						// After the initial response, further responses are sent through a side-channel.
						if (dependencies?.isServer && isSsrHandoverIncrementsEnabled()) {
							sink.complete();
						}
					},
					error: (error: Error) => {
						if (!firstResponseUsed) {
							firstResponseUsed = true;
							// todo MAGMA-3172: check control flows for fetch failure cases on server + client. Seems weird that
							// the previous construction of the function would not send a signal that the response was received
							// in the error case...
							// resolveFirstResponse();
						}
						loggingAndMetrics?.logError(error);
						sink.error(error);
					},
					complete: () => {
						if (responseShouldHang) {
							return;
						}
						finalise();
					},
				});
			};

			// On client...
			if (!dependencies?.isServer && requestID != null) {
				// read cache from window.SPA_STATE if available
				const ssrCache = dependencies?.queryResponseCache?.get(requestID, variables);
				if (!isNil(ssrCache)) {
					dependencies?.queryResponseCache?.delete(requestID, variables);
					sink.next(ssrCache);
					if (isSsrHandoverIncrementsEnabled() && 'hasNext' in ssrCache && ssrCache.hasNext) {
						// TODO MAGMA-3017: what about SSR fallback mode, or scenarios where SSR has partially failed?
						// TODO MAGMA-3204: improve robustness. Should be using a middle-man module rather than client dispatcher directly.
						// Receive response events as they are streamed from the server.
						getClientDispatcher().register(sinkID, handleResponse);
						getClientDispatcher().onAbort(sinkID, ({ reason }) => {
							if (!sink.closed) {
								dependencies?.handleAbort?.(request, reason);
								// if server throws an exception (such as SSR hanging up due to deadline exceeded)
								// refetch multipart data again via network
								getData();
							}
						});
					} else {
						finalise();
					}
					return;
				}
			}

			getData();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			loggingAndMetrics?.logError(error);
			sink.error(error);
		}
	});
};

export function enforceProductNonNullability(
	response: GraphQLSingularResponse,
): GraphQLSingularResponse {
	if (response.data) {
		// Enforce non-nullability of the product namespaces. This is an intermediary solution to
		// prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
		// to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
		let sanitisedData = Object.assign({}, response.data);
		const productKeys = ['jira', 'customerService', 'graphStore'];

		for (const product of productKeys) {
			if (sanitisedData[product] === null) {
				delete sanitisedData[product];
			}
		}

		if (Object.keys(sanitisedData).length === 0) {
			// @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Dataa
			sanitisedData = null;
		}

		response.data = sanitisedData;
	}

	return response;
}
